/**
 * API em api.automais.io no dev e na produção.
 * Override opcional: .env.local → VITE_API_BASE_URL=http://localhost:5000/api
 */
export const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://api.automais.io/api'

export function getApiBaseUrl() {
  return API_BASE_URL
}

/** @deprecated não altera a URL da API */
export function isProduction() {
  if (typeof window === 'undefined') return false
  try {
    const h = window.location.hostname
    return h === 'automais.io' || h === 'www.automais.io'
  } catch {
    return false
  }
}

function apiUrlParts() {
  try {
    const u = new URL(API_BASE_URL)
    return {
      host: u.host,
      pathPrefix: u.pathname.replace(/\/$/, '') || '/api',
      wsProto: u.protocol === 'https:' ? 'wss:' : 'ws:',
    }
  } catch {
    return { host: 'api.automais.io', pathPrefix: '/api', wsProto: 'wss:' }
  }
}

export const SIGNALR_BASE_URL = (() => {
  const { host, pathPrefix, wsProto } = apiUrlParts()
  const httpProto = wsProto === 'wss:' ? 'https:' : 'http:'
  return `${httpProto}//${host}${pathPrefix}/hubs`
})()

export function getSignalRBaseUrl() {
  return SIGNALR_BASE_URL
}

/** WebSocket RouterOS (mesmo host da API) */
export function getRouterOsWsUrl(routerId) {
  if (!routerId) {
    throw new Error('routerId é obrigatório para conectar ao WebSocket RouterOS')
  }
  const { host, pathPrefix, wsProto } = apiUrlParts()
  return `${wsProto}//${host}${pathPrefix}/ws/routeros/${routerId}`
}

export function getRouterOsWsUrlDefault() {
  const { host, pathPrefix, wsProto } = apiUrlParts()
  return `${wsProto}//${host}${pathPrefix}/ws/routeros`
}

/**
 * WebSocket Hosts / SSH (mesmo host da API).
 * O JWT vai na query (?access_token=) porque o browser não envia Authorization no handshake WebSocket.
 * @param {string} hostId
 * @param {string | null | undefined} [accessToken] — se omitido, usa localStorage.token no browser
 */
export function getHostsWsUrl(hostId, accessToken) {
  if (!hostId) {
    throw new Error('hostId é obrigatório para o WebSocket Hosts')
  }
  const token =
    accessToken !== undefined && accessToken !== null
      ? accessToken
      : typeof window !== 'undefined'
        ? localStorage.getItem('token')
        : null
  if (!token || !String(token).trim()) {
    throw new Error(
      'Sessão não encontrada: faça login novamente para usar o console do host.'
    )
  }
  const { host, pathPrefix, wsProto } = apiUrlParts()
  const q = new URLSearchParams({ access_token: String(token).trim() })
  return `${wsProto}//${host}${pathPrefix}/ws/hosts/${hostId}?${q.toString()}`
}

/**
 * WebSocket do console MQTT do device (stream filtrado por chipId no backend).
 * @param {string} deviceId
 * @param {string | null | undefined} [accessToken]
 */
export function getDeviceConsoleWsUrl(deviceId, accessToken) {
  if (!deviceId) {
    throw new Error('deviceId é obrigatório para o console do device')
  }
  const token =
    accessToken !== undefined && accessToken !== null
      ? accessToken
      : typeof window !== 'undefined'
        ? localStorage.getItem('token')
        : null
  if (!token || !String(token).trim()) {
    throw new Error('Sessão não encontrada: faça login novamente.')
  }
  const { host, pathPrefix, wsProto } = apiUrlParts()
  const q = new URLSearchParams({ access_token: String(token).trim() })
  return `${wsProto}//${host}${pathPrefix}/ws/iot-devices/${encodeURIComponent(deviceId)}/mqtt-console?${q.toString()}`
}

/**
 * WebSocket display remoto (VNC/RFB via túnel). Mesmo host da API; JWT na query.
 * @param {string} hostId
 * @param {string | null | undefined} [accessToken]
 */
/**
 * URL absoluta da UI web do device (proxy HTTP na API). JWT na query para o iframe.
 * @param {string} devEui DevEUI do device (hex, ex.: 16 caracteres LoRaWAN)
 * @param {string} [relativePath] caminho após /web-ui/ (ex.: "style.css" ou "api/foo")
 * @param {string | null | undefined} [accessToken]
 */
export function getDeviceWebUiUrl(devEui, relativePath = '', accessToken) {
  if (!devEui) {
    throw new Error('DevEUI é obrigatório para abrir a UI remota do device')
  }
  const token =
    accessToken !== undefined && accessToken !== null
      ? accessToken
      : typeof window !== 'undefined'
        ? localStorage.getItem('token')
        : null
  if (!token || !String(token).trim()) {
    throw new Error('Sessão não encontrada: faça login novamente.')
  }
  const base = API_BASE_URL.replace(/\/$/, '')
  let pathSuffix = ''
  if (relativePath && relativePath !== '/') {
    const clean = String(relativePath).replace(/^\/+/, '')
    pathSuffix = clean ? `/${clean}` : ''
  }
  const enc = encodeURIComponent(String(devEui).trim())
  const url = new URL(`${base}/iot-devices/${enc}/web-ui${pathSuffix || '/'}`)
  url.searchParams.set('access_token', String(token).trim())
  // Fuso: contrato "UTC no fio, navegador localiza" — os tempos voltam em UTC com 'Z' e o
  // template (new Date(...).toLocaleString) exibe no fuso do navegador = fuso do viewer.
  return url.toString()
}

/**
 * URL absoluta da tela (template webui) de um ManagedDevice. JWT na query para o 1º documento;
 * os sub-pedidos usam o cookie de sessão escopado a …/web-ui.
 * @param {string} managedDeviceId
 * @param {string | null | undefined} [accessToken]
 */
export function getManagedDeviceWebUiUrl(managedDeviceId, accessToken) {
  if (!managedDeviceId) {
    throw new Error('managedDeviceId é obrigatório para abrir a tela do device')
  }
  const token =
    accessToken !== undefined && accessToken !== null
      ? accessToken
      : typeof window !== 'undefined'
        ? localStorage.getItem('token')
        : null
  if (!token || !String(token).trim()) {
    throw new Error('Sessão não encontrada: faça login novamente.')
  }
  const base = API_BASE_URL.replace(/\/$/, '')
  const enc = encodeURIComponent(String(managedDeviceId).trim())
  const url = new URL(`${base}/managed-devices/${enc}/web-ui/`)
  url.searchParams.set('access_token', String(token).trim())
  return url.toString()
}

export function getRemoteDisplayWsUrl(hostId, accessToken) {
  if (!hostId) {
    throw new Error('hostId é obrigatório para o display remoto')
  }
  const token =
    accessToken !== undefined && accessToken !== null
      ? accessToken
      : typeof window !== 'undefined'
        ? localStorage.getItem('token')
        : null
  if (!token || !String(token).trim()) {
    throw new Error(
      'Sessão não encontrada: faça login novamente para usar o display remoto.'
    )
  }
  const { host, pathPrefix, wsProto } = apiUrlParts()
  const q = new URLSearchParams({ access_token: String(token).trim() })
  return `${wsProto}//${host}${pathPrefix}/ws/remote/${hostId}?${q.toString()}`
}

/**
 * URL WebSocket para receber frames JPEG do display Android.
 * Endpoint: /api/ws/android-display/{hostId}
 */
export function getAndroidDisplayWsUrl(hostId, accessToken) {
  if (!hostId) {
    throw new Error('hostId é obrigatório para o display Android')
  }
  const token =
    accessToken !== undefined && accessToken !== null
      ? accessToken
      : typeof window !== 'undefined'
        ? localStorage.getItem('token')
        : null
  if (!token || !String(token).trim()) {
    throw new Error(
      'Sessão não encontrada: faça login novamente para usar o display Android.'
    )
  }
  const { host, pathPrefix, wsProto } = apiUrlParts()
  const q = new URLSearchParams({ access_token: String(token).trim() })
  return `${wsProto}//${host}${pathPrefix}/ws/android-display/${hostId}?${q.toString()}`
}

/**
 * URL WebSocket do viewer de tela do MDM (tablets/celulares do tipo "mobile").
 * Endpoint: /api/ws/mdm/screen/{deviceId} — frames JPEG binários + status em
 * texto; o viewer envia eventos de input normalizados (Automais.MDM/docs/CONTRACT.md §4).
 */
export function getMdmScreenWsUrl(deviceId, accessToken) {
  if (!deviceId) {
    throw new Error('deviceId é obrigatório para a tela remota')
  }
  const token =
    accessToken !== undefined && accessToken !== null
      ? accessToken
      : typeof window !== 'undefined'
        ? localStorage.getItem('token')
        : null
  if (!token || !String(token).trim()) {
    throw new Error('Sessão não encontrada: faça login novamente para ver a tela remota.')
  }
  const { host, pathPrefix, wsProto } = apiUrlParts()
  const q = new URLSearchParams({ access_token: String(token).trim() })
  return `${wsProto}//${host}${pathPrefix}/ws/mdm/screen/${deviceId}?${q.toString()}`
}

/**
 * URL WebSocket para o signaling WebRTC unificado (Windows e Android).
 * Endpoint: /api/ws/webrtc-signal/{hostId}
 */
export function getWebRtcSignalWsUrl(hostId, accessToken) {
  if (!hostId) {
    throw new Error('hostId é obrigatório para o signaling WebRTC')
  }
  const token =
    accessToken !== undefined && accessToken !== null
      ? accessToken
      : typeof window !== 'undefined'
        ? localStorage.getItem('token')
        : null
  if (!token || !String(token).trim()) {
    throw new Error(
      'Sessão não encontrada: faça login novamente para usar o display remoto.'
    )
  }
  const { host, pathPrefix, wsProto } = apiUrlParts()
  const q = new URLSearchParams({ access_token: String(token).trim() })
  return `${wsProto}//${host}${pathPrefix}/ws/webrtc-signal/${hostId}?${q.toString()}`
}

/**
 * Download do agente Windows (exe) via API.
 * JWT em query porque o download costuma ser feito por link direto.
 */
export function getWindowsAgentDownloadUrl(accessToken) {
  const token =
    accessToken !== undefined && accessToken !== null
      ? accessToken
      : typeof window !== 'undefined'
        ? localStorage.getItem('token')
        : null
  if (!token || !String(token).trim()) {
    throw new Error('Sessão não encontrada: faça login novamente.')
  }
  const base = API_BASE_URL.replace(/\/$/, '')
  const q = new URLSearchParams({ access_token: String(token).trim() })
  return `${base}/hosts/windows-agent/download?${q.toString()}`
}

/**
 * Download "genérico" do Connect.IO via endpoint de releases.
 * Serve o exe puro (sem overlay de enrollment) — usado pelo botão de download no header,
 * disponível para qualquer usuário autenticado.
 */
export function getReleasesWindowsAgentDownloadUrl(accessToken) {
  const token =
    accessToken !== undefined && accessToken !== null
      ? accessToken
      : typeof window !== 'undefined'
        ? localStorage.getItem('token')
        : null
  if (!token || !String(token).trim()) {
    throw new Error('Sessão não encontrada: faça login novamente.')
  }
  const base = API_BASE_URL.replace(/\/$/, '')
  const q = new URLSearchParams({ access_token: String(token).trim() })
  return `${base}/releases/windows-agent/download?${q.toString()}`
}
